import moment from 'moment';
import { AxiosCompanyDashboardFleetIoConfigsClient } from '../proxy/ProxyClients';
import FleetIoService from './FleetIoService';
import EnumService from './EnumService';
import FormattedShift from '../models/FormattedShift';
import ShiftsService from './ShiftsService';
import { FleetIoFormType } from '../proxy/proxy';

interface PrecheckForm {
  id: number;
  name: string;
}

interface FleetIoInspectionRecord {
  id: number;
  started_at: string;
  submitted_at: string;
  failed_items: number;
  starting_latitude: number | null;
  starting_longitude: number | null;
  submitted_latitude: number | null;
  submitted_longitude: number | null;
  date: string;
  state: number;
  inspection_form: {
    id: number;
    title: string;
    description: string;
  };
  user: {
    id: number;
    name: string;
    email: string | null;
    group_id: number;
    default_image_url: string | null;
  };
  vehicle: {
    id: number;
    name: string;
    color: string;
    license_plate: string;
    make: string;
    model: string;
    registration_expiration_month: number;
    registration_state: string;
    trim: string;
    vin: string;
    year: number;
    default_image_url_small: string | null;
  };
}

interface FleetIoResponse {
  start_cursor: string;
  next_cursor: string | null;
  per_page: number;
  estimated_remaining_count: number;
  filtered_by: Array<any>;
  sorted_by: Array<any>;
  records: FleetIoInspectionRecord[];
}

interface FormTypes {
  precheckForms: PrecheckForm[];
  fridgeInspectionForms: PrecheckForm[];
}

interface FormTypesResult {
  precheckSubmissions: FleetIoInspectionRecord[];
  fridgeInspectionSubmissions: FleetIoInspectionRecord[];
}

interface SubmissionResult {
  precheckCompleteShifts: Array<{ shiftId: number; submissionId: number; state: number }>,
  fridgeInspectionCompleteShifts: Array<{ shiftId: number; submissionId: number; state: number }>,
}

export class FleetIoFormsService {
  static async fetchAllFormsData(): Promise<FormTypesResult> {
    try {
      const states = await EnumService.getEnums('states');
      const configResponse = await AxiosCompanyDashboardFleetIoConfigsClient.get();

      const fleetIoFormTypes = await EnumService.getEnums('fleetIoFormTypes');
      const precheckFleetIoFormTypeId = fleetIoFormTypes.find((ft) => ft.value === 'Precheck')?.id || 0;
      const fridgeInspectionFleetIoFormTypeId = fleetIoFormTypes.find((ft) => ft.value === 'FridgeInspection')?.id || 0;

      const precheckForms = configResponse?.config?.forms?.filter((x) => x.fleetIoFormType === precheckFleetIoFormTypeId)?.map((element) => ({
        id: element.id || 0,
        name: element.name || '',
        type: 'precheck',
        stateIds: element.stateIds || []
      })) || [];

      const fridgeForms = configResponse?.config?.forms?.filter((x) => x.fleetIoFormType === fridgeInspectionFleetIoFormTypeId)?.map((element) => ({
        id: element.id || 0,
        name: element.name || '',
        type: 'fridge',
        stateIds: element.stateIds || []
      })) || [];

      const allForms = [...precheckForms, ...fridgeForms];

      const stateFormCombinations = allForms.flatMap((form) =>
        form.stateIds.map((stateId: number) => ({
          stateId,
          form,
          payload: {
            actionId: 902,
            stateId,
            queryParams: {
              inspection_form_id: form.id.toString(),
            },
          },
        })));

      // Fetch all records in parallel
      const recordsPromises = stateFormCombinations.map(async ({ stateId, form, payload }) => {
        try {
          const response = await FleetIoService.fleetIoPage(payload);
          if (response?.records?.length > 0) {
            return response.records.map((record: FleetIoResponse) => ({
              ...record,
              state: stateId,
              formType: form.type
            }));
          }
          return [];
        } catch (error) {
          return [];
        }
      });

      const allRecordsArrays = await Promise.all(recordsPromises);
      const allRecords = allRecordsArrays.flat();

      // Sort and split records by form type
      const sortedRecords = allRecords.sort((a, b) =>
        new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime());

      return {
        precheckSubmissions: sortedRecords.filter((record) => record.formType === 'precheck'),
        fridgeInspectionSubmissions: sortedRecords.filter((record) => record.formType === 'fridge')
      };
    } catch (error) {
      console.error('Error in fetchAllFormsData:', error);
      throw error;
    }
  }

  static async matchSubmissionsWithVehicles(shiftRows: FormattedShift[]): Promise<SubmissionResult> {
    try {
      const { precheckSubmissions, fridgeInspectionSubmissions } = await this.fetchAllFormsData();

      // Get FleetIo form types from EnumService
      const fleetIoFormTypes = await EnumService.getEnums('fleetIoFormTypes');
      const precheckFormTypeId = fleetIoFormTypes.find((ft) => ft.value === 'Precheck')?.id || 1;
      const fridgeInspectionFormTypeId = fleetIoFormTypes.find((ft) => ft.value === 'FridgeInspection')?.id || 2;

      const result: SubmissionResult = {
        precheckCompleteShifts: [],
        fridgeInspectionCompleteShifts: []
      };

      return shiftRows.reduce((acc: SubmissionResult, shift) => {
        const vehicleRego = shift.vehicle?.registrationNumber;
        const scheduledMoment = moment.utc(shift.scheduledStartActualUTC.replace('Z', ''));

        if (vehicleRego) {
          // Check precheck submissions
          const precheckMatch = precheckSubmissions.find((submission) => {
            const submissionMoment = moment.utc(submission.submitted_at);
            if (submission.vehicle.license_plate === vehicleRego) {
              console.log(`Checking precheck submission for vehicle ${vehicleRego} ${submission.vehicle.license_plate} at ${scheduledMoment} against form submitted at ${submission.submitted_at} ${submissionMoment}`);
              console.log(
                submission.vehicle.license_plate === vehicleRego,
                submissionMoment.isBetween(
                  scheduledMoment.clone().subtract(2, 'hour'),
                  scheduledMoment,
                  undefined,
                  '[)'
                )
              );
            }

            return (
              submission.vehicle.license_plate === vehicleRego
              && submissionMoment.isBetween(
                scheduledMoment.clone().subtract(2, 'hour'),
                scheduledMoment,
                undefined,
                '[)'
              )
            );
          });

          if (precheckMatch) {
            acc.precheckCompleteShifts.push({
              shiftId: shift.id,
              submissionId: precheckMatch.id,
              state: precheckMatch.state
            });

            if (shift.shiftFleetIoFormSubmissions?.some((sub) => sub.fleetIoFormSubmissionId === precheckMatch.id.toString())) {
              console.log(`Shift ${shift.id} already associated with precheck submission ${precheckMatch.id}`);
            } else {
              // Associate shift with FleetIo form submission
              ShiftsService.associateShiftFleetIoFormSubmission({
                fleetIoFormSubmissionId: precheckMatch.id.toString(),
                fleetIoFormTitle: precheckMatch.inspection_form?.title,
                fleetIoFormType: precheckFormTypeId,
                submittedToFleetIoOnUtc: precheckMatch.submitted_at,
                shiftId: shift.id
              }).catch((error) => {
                console.error('Error associating precheck submission with shift:', error);
              });
            }
          }

          // Check fridge submissions
          const fridgeMatch = fridgeInspectionSubmissions.find((submission) => {
            const submissionMoment = moment.utc(submission.submitted_at);

            if (submission.vehicle.license_plate === vehicleRego) {
              console.log(`Checking fridge submission for vehicle ${vehicleRego} ${submission.vehicle.license_plate} at ${scheduledMoment} against form submitted at ${submissionMoment}`, submission);
              console.log(
                submission.vehicle.license_plate === vehicleRego,
                submissionMoment.isBetween(
                  scheduledMoment.clone().subtract(2, 'hour'),
                  scheduledMoment,
                  undefined,
                  '[)'
                )
              );
            }

            return (
              submission.vehicle.license_plate === vehicleRego
              && submissionMoment.isBetween(
                scheduledMoment.clone().subtract(2, 'hour'),
                scheduledMoment,
                undefined,
                '[)'
              )
            );
          });

          if (fridgeMatch) {
            acc.fridgeInspectionCompleteShifts.push({
              shiftId: shift.id,
              submissionId: fridgeMatch.id,
              state: fridgeMatch.state
            });

            if (shift.shiftFleetIoFormSubmissions?.some((sub) => sub.fleetIoFormSubmissionId === fridgeMatch.id.toString())) {
              console.log(`Shift ${shift.id} already associated with fridge submission ${fridgeMatch.id}`);
            } else {
              // Associate shift with FleetIo form submission
              ShiftsService.associateShiftFleetIoFormSubmission({
                fleetIoFormSubmissionId: fridgeMatch.id.toString(),
                fleetIoFormTitle: fridgeMatch.inspection_form?.title,
                fleetIoFormType: fridgeInspectionFormTypeId,
                submittedToFleetIoOnUtc: fridgeMatch.submitted_at,
                shiftId: shift.id
              }).catch((error) => {
                console.error('Error associating fridge inspection submission with shift:', error);
              });
            }
          }
        }

        return acc;
      }, result);
    } catch (error) {
      console.error('Error matching submissions:', error);
      throw error;
    }
  }
}

export default FleetIoFormsService;
